<template>
  <div>
    <h3 class="text-center text-primary">
      {{ trialRequestUnavailable ? "Unable to proceed" : "Thank you!" }}
    </h3>

    <div class="d-flex gap-4 mb-5">
      <CheckMark class="text-success" style="width: 90px; height: 90px" />

      <div>
        <p>You have requested:</p>

        <ul class="text-bold">
          <li v-if="defaultProduct === 'minimash_evidenceme_bundle'">
            MiniMash and Evidence Me bundle
          </li>

          <template v-else>
            <li
              v-if="
                (products.includes('purplemash') &&
                  !products.includes('minimash')) ||
                products.includes('purplemash_corona')
              "
            >
              Purple Mash
            </li>
            <li
              v-if="
                (products.includes('purplemash') ||
                  products.includes('serialmash')) &&
                products.includes('minimash')
              "
            >
              Purple Mash + Mini Mash*
            </li>
            <li
              v-if="
                !products.includes('purplemash') &&
                products.includes('minimash') &&
                !products.includes('serialmash')
              "
            >
              Mini Mash
            </li>
            <li
              v-for="choice in filteredProductLicenceChoices"
              :key="choice.field_name"
            >
              {{ choice.name }}
            </li>
          </template>
        </ul>
      </div>
    </div>

    <template v-if="existingLicenceError">
      <p v-if="existingLicenceError === 'customer'">
        Since your school already uses our resources we need to confirm this
        request before we set up access.
      </p>
      <p v-else>
        Since you recently accessed our resources we need to confirm your
        request.
      </p>
      <p>
        This is for security as our platforms share data between each other to
        save our customers time.
      </p>
    </template>

    <p v-if="trialRequestUnavailable">
      We are unable to process your request at this time. Please
      <a href="/contact">contact us</a> for additional assistance.
    </p>
    <template v-else>
      <p
        v-if="
          products.includes('minimash') &&
          !products.includes('purplemash') &&
          products.includes('serialmash')
        "
      >
        *Mini Mash cannot be used alongside Serial Mash so we've set you up with
        a Purple Mash trial instead. Mini Mash comes included with any Purple
        Mash subscription!
      </p>
      <p
        v-if="products.includes('minimash') && products.includes('purplemash')"
      >
        *Mini Mash comes included with any Purple Mash subscription!
      </p>

      <p>
        <template v-if="automaticallySetupTrials?.length === 0">
          {{ noAutomatedTrialText }}
        </template>
        <template v-else>
          {{ automatedTrialText }}
          <span v-if="products.includes('buildaprofile')">
            Please note that currently Evidence Me trials can take a few days to
            process.
          </span>
        </template>
      </p>
    </template>

    <div class="d-grid">
      <UIButton variant="success" @click="dialog.hide()">Close</UIButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { trialFormSettings } from "@/app/settings";
import CheckMark from "@/assets/icons/checkmark.svg?component";
import UIButton from "@/components/UI/UIButton.vue";
import { useDialog } from "@/components/UI/dialog/useDialog";
import { ProductLicenceChoice } from "@/types";
import { computed } from "vue";
import { ProductTrialDialogId } from "./ProductTrialDialog.vue";

type Props = {
  automatedTrialText: string;
  defaultProduct: string;
  noAutomatedTrialText: string;
  products: string[];
  automaticallySetupTrials?: string[];
  existingLicenceError?: string | null;
  trialRequestUnavailable?: boolean;
};

const props = defineProps<Props>();
const dialog = useDialog(ProductTrialDialogId);

// Purple Mash and minimash have special logic for how their
// product names are displayed so they are excluded.
const filteredProductLicenceChoices = computed<ProductLicenceChoice[]>(() => {
  return trialFormSettings.productLicenceChoices.filter((product) => {
    return (
      props.products.includes(product.field_name) &&
      ["purplemash", "minimash", "purplemash_corona"].indexOf(
        product.field_name
      ) < 0
    );
  });
});
</script>

<style lang="scss">
.done-check {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  font-size: 36px;
  color: white;
  background-color: var(--success);
  border-radius: 50%;
  text-align: center;
}
</style>
